import React, { Component } from 'react';
import PropTypes from 'prop-types';
import GoogleMap from 'react-google-map';

import lodashUniq from 'lodash/uniq';
import lodashDebounce from 'lodash/debounce';
import lodashIsEqual from 'lodash/isEqual';

import { isSelfShowingListing, hasListing3DTour } from 'common/listingHelpers';

import withGoogleMaps from 'client/helpers/withGoogleMaps';

import ListingActions from 'client/actions/Listing';
import SearchActions from 'client/actions/Search';
import UserActions from 'client/actions/User';

import { defaultPolygonOptions, defaultMapOptions } from 'client/constants/mapOptions';

import {
  getMapArea,
  getMarkerShape,
  getMarkerIconConfig,
  getMarkerLabelConfig,
  fitMarkerOnMap,
  getCenterByArea,
  GOOGLE_MAPS_MARKER_THEME_LISTING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED_VIEWED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR_VIEWED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR,
  GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR_VIEWED,
} from 'client/helpers/googleMapsHelpers';

import branch from 'client/core/branch';

const getMarkerConfig = ({
  map,
  googleMaps,
  listing,
  viewedListings,
  viewingListingId,
  currentUser,
  bookmarksByListingId,
}) => {
  const markerShape = getMarkerShape(map.getZoom());
  const isSelfShowing = isSelfShowingListing(listing);
  const has3dTour = hasListing3DTour(listing);
  let markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING;

  if (viewingListingId === listing.id) {
    markerTheme = has3dTour
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR_VIEWING
      : bookmarksByListingId.includes(`${listing.id}`)
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED_VIEWING
      : isSelfShowing
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR_VIEWING
      : GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWING;
  } else if (viewedListings.includes(listing.id)) {
    markerTheme = has3dTour
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR_VIEWED
      : bookmarksByListingId.includes(`${listing.id}`)
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED_VIEWED
      : isSelfShowing
      ? GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR_VIEWED
      : GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWED;
  } else if (has3dTour) {
    markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR;
  } else if (isSelfShowing) {
    markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR;
  } else if (bookmarksByListingId.includes(`${listing.id}`)) {
    markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED;
  }

  return {
    icon: getMarkerIconConfig({
      markerShape,
      googleMaps,
      markerTheme: markerTheme,
    }),
    label: getMarkerLabelConfig({
      markerShape,
      listing,
      currentUser,
    }),
  };
};

@withGoogleMaps
@branch({
  viewedListings: ['listingsMapView', 'viewedListings'],
  viewingListingId: ['listingsMapView', 'viewingListingId'],
  mapArea: ['listingsMapView', 'mapArea'],
  mapZoomLevel: ['listingsMapView', 'mapZoomLevel'],
  mapChanged: ['listingsMapView', 'mapChanged'],
  mapCenter: ['listingsMapView', 'mapCenter'],
  currentUser: ['currentUser'],
  showRedoSearch: ['listingsMapView', 'showRedoSearch'],
  currentUserMarket: ['markets', 'currentUserMarket'],
})
class SrListingsMapView extends Component {
  static propTypes = {
    listings: PropTypes.array.isRequired,
    viewedListings: PropTypes.array.isRequired,
    viewingListingId: PropTypes.number,
    mapZoomLevel: PropTypes.number,
    mapCenter: PropTypes.object,
    mapChanged: PropTypes.bool.isRequired,
    currentUser: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    googleMaps: PropTypes.object,
    isMobile: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    showRedoSearch: PropTypes.bool.isRequired,
    bookmarksByListingId: PropTypes.array,
    currentUserMarket: PropTypes.object.isRequired,
  };

  constructor() {
    super();

    this.state = {
      map: null,
      markers: new Map(),
    };
  }

  componentDidMount() {
    this.props.dispatch(({ state }) => {
      state.set(['listingsViewMode'], 'map');
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      (this.props.viewingListingId && !nextProps.viewingListingId) ||
      this.props.bookmarksByListingId.length !== nextProps.bookmarksByListingId.length
    ) {
      const { map, markers } = this.state;
      const { viewingListingId, googleMaps, viewedListings, listings, currentUser } = this.props;
      const viewedMarker = markers.get(viewingListingId);
      if (viewedMarker) {
        viewedMarker.setOptions(
          getMarkerConfig({
            map,
            googleMaps,
            viewedListings,
            viewingListingId: nextProps.viewingListingId,
            listing: listings.find((listing) => listing.id === viewingListingId),
            currentUser,
            bookmarksByListingId: nextProps.bookmarksByListingId,
          }),
        );
      }
    }

    this._removeOldMarkers(nextProps.listings);
    this._addNewMarkers(nextProps.listings);
    this._shouldRedoSearch(nextProps);
  }

  _shouldRedoSearch = (nextProps) => {
    if (!nextProps.showRedoSearch && this.props.showRedoSearch) {
      this._getListings({
        showResultsMatched: true,
      });
    }
  };

  _isMapNotConfigurated = () => {
    const { mapCenter, currentUser } = this.props;
    return !mapCenter && !currentUser.searchFilters.mapZoomLevel;
  };

  _getListings = (options = {}) => {
    const { dispatch } = this.props;
    const { showResultsMatched } = options;

    dispatch(ListingActions.getList).then(() => {
      if (showResultsMatched) {
        this.props.dispatch(SearchActions.setMapViewValue, 'showResultsMatched', true);
      }
    });
  };

  _onLoadedMap = (googleMaps, map) => {
    const { listings, location, currentUser, currentUserMarket } = this.props;
    if (location.search.includes('sunroomMapArea')) {
      const polygon = new googleMaps.Polygon({
        paths: currentUserMarket.serviceZone.coordinates.map((coord) => ({
          lng: coord[0],
          lat: coord[1],
        })),
        ...defaultPolygonOptions,
      });

      polygon.setMap(map);
    }

    this.setState({ map }, () => {
      if (listings.length) {
        this._addNewMarkers(listings);
      }

      if (this._isMapNotConfigurated()) {
        const bounds = Array.from(currentUser.searchFilters.area).reduce(
          (bound, coord) => bound.extend(new googleMaps.LatLng(coord[1], coord[0])),
          new googleMaps.LatLngBounds(),
        );

        map.fitBounds(bounds);
      }
    });

    if (!listings.length) {
      this._getListings();
    }

    map.addListener('zoom_changed', this._onZoomChange);
    map.addListener('center_changed', this._onCenterChange);
    map.addListener('click', this._onClickMap);
    map.addListener('bounds_changed', this._onBoundsChange);
  };

  _onBoundsChange = lodashDebounce(() => {
    const { map } = this.state;
    const { mapArea, mapChanged, isMobile } = this.props;
    const newMapArea = getMapArea({ map });

    if (lodashIsEqual(mapArea, newMapArea)) {
      return;
    }

    this.props.dispatch(SearchActions.setMapViewValue, 'mapArea', newMapArea);
    this.props.dispatch(SearchActions.setMapViewValue, 'mapChanged', true);

    // Load listings for first time
    if (!mapChanged || !isMobile) {
      this._getListings();
    }
  }, 200);

  _onZoomChange = () => {
    const { map, markers } = this.state;
    const {
      dispatch,
      googleMaps,
      listings,
      viewedListings,
      viewingListingId,
      mapZoomLevel,
      currentUser,
      bookmarksByListingId,
    } = this.props;
    const newZoomLevel = map.getZoom();
    const newMarkerShape = getMarkerShape(newZoomLevel);

    dispatch(SearchActions.setMapZoomLevel, newZoomLevel);

    if (newMarkerShape !== getMarkerShape(mapZoomLevel)) {
      listings.forEach((listing) => {
        if (markers.has(listing.id)) {
          const marker = markers.get(listing.id);
          marker.setOptions(
            getMarkerConfig({
              map,
              googleMaps,
              viewedListings,
              viewingListingId,
              listing,
              currentUser,
              bookmarksByListingId,
            }),
          );
        }
      });
    }
  };

  _onClickMap = () => {
    this.props.dispatch(SearchActions.setMapViewValue, 'viewingListingId', null);
  };

  _onCenterChange = lodashDebounce(() => {
    const { dispatch } = this.props;
    const mapArea = getMapArea({ map: this.state.map });

    dispatch(SearchActions.setMapCenter, {
      lat: this.state.map.getCenter().lat(),
      lng: this.state.map.getCenter().lng(),
      mapArea: mapArea,
    });
  }, 400);

  _removeOldMarkers = (listings) => {
    const { markers } = this.state;

    markers.forEach((marker, markerId) => {
      const isMarkerUsed = listings.some((listing) => listing.id === markerId);
      if (!isMarkerUsed) {
        marker.setMap(null);
        markers.delete(markerId);
      }
    });

    this.setState({ markers });
  };

  _addNewMarkers = (listings) => {
    const { markers } = this.state;

    if (!listings.length) {
      return;
    }

    let hasAddedNewListings = 0;

    listings.forEach((listing) => {
      if (!markers.has(listing.id)) {
        markers.set(listing.id, this._addMarker(listing));
        hasAddedNewListings += 1;
      }
    });

    if (hasAddedNewListings) {
      this.setState({ markers });
    }
  };

  _addMarker = (listing) => {
    const { map } = this.state;
    const {
      googleMaps,
      viewedListings,
      viewingListingId,
      currentUser,
      bookmarksByListingId,
    } = this.props;

    const marketOptions = {
      position: {
        lat: listing.locationLatitude,
        lng: listing.locationLongitude,
      },
      ...getMarkerConfig({
        map,
        googleMaps,
        viewedListings,
        viewingListingId,
        listing,
        currentUser,
        bookmarksByListingId,
      }),
    };

    const marker = new googleMaps.Marker({
      map: map,
      ...marketOptions,
    });

    googleMaps.event.addListener(marker, 'click', () => {
      this._onClickMarker(listing.id);
    });

    return marker;
  };

  _onClickMarker = (viewingListingId) => {
    const { map, markers } = this.state;
    const {
      googleMaps,
      viewedListings,
      listings,
      dispatch,
      currentUser,
      isMobile,
      bookmarksByListingId,
    } = this.props;
    const viewingListing = listings.find((listing) => listing.id === viewingListingId);

    viewedListings.forEach((viewedListingId) => {
      const viewedMarker = markers.get(viewedListingId);
      if (viewedMarker && viewedListingId !== viewingListingId) {
        viewedMarker.setOptions(
          getMarkerConfig({
            map,
            googleMaps,
            viewedListings,
            viewingListingId,
            listing: listings.find((listing) => listing.id === viewedListingId),
            currentUser,
            bookmarksByListingId,
          }),
        );
      }
    });

    const marker = markers.get(viewingListingId);
    marker.setOptions(
      getMarkerConfig({
        map,
        googleMaps,
        viewedListings,
        viewingListingId,
        listing: viewingListing,
        currentUser,
        bookmarksByListingId,
      }),
    );

    fitMarkerOnMap({ marker, googleMaps, isMobile, map });

    dispatch(({ state }) => {
      state.set(['listingsMapView', 'showResultsMatched'], false);
      state.set(['listingsMapView', 'viewingListingId'], viewingListingId);
      state.set(
        ['listingsMapView', 'viewedListings'],
        lodashUniq([...viewedListings, viewingListingId]),
      );
    });
    dispatch(UserActions.updateViewedListings, {
      viewedListingId: viewingListingId,
    });
  };

  render() {
    const { googleMaps, mapZoomLevel, mapCenter, isMobile, currentUser } = this.props;

    return (
      <GoogleMap
        googleMaps={googleMaps}
        center={
          mapCenter ||
          getCenterByArea({ googleMaps, area: this.props.currentUser.searchFilters.area })
        }
        zoom={
          this._isMapNotConfigurated() ? 18 : mapZoomLevel || currentUser.searchFilters.mapZoomLevel
        }
        maxZoom={this._isMapNotConfigurated() ? '18' : '16'}
        disableDefaultUI
        zoomControl={!isMobile}
        zoomControlOptions={{
          position: googleMaps.ControlPosition.RIGHT_TOP,
        }}
        gestureHandling="greedy"
        clickableIcons={false}
        onLoaded={this._onLoadedMap}
        options={defaultMapOptions}
      />
    );
  }
}

SrListingsMapView.displayName = 'SrListingsMapView';

export default SrListingsMapView;
